import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, parseISO, isValid, subDays } from 'date-fns';
import {
  Gauge, CheckCircle, AlertTriangle, IndianRupee, Package,
  Loader2, RefreshCw, Phone, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const safeFormat = (val, fmt) => {
  if (!val) return '—';
  try {
    const d = typeof val === 'string' ? parseISO(val) : val;
    return isValid(d) ? format(d, fmt) : '—';
  } catch { return '—'; }
};

const KpiCard = ({ label, sub, count, amount, color, icon: Icon, active, onClick }) => (
  <button onClick={onClick}
    className={`text-left bg-white border-2 rounded-2xl p-5 shadow-sm transition-all hover:-translate-y-0.5 ${
      active ? color.border + ' shadow-md' : 'border-slate-200'
    }`}>
    <div className="flex items-center justify-between mb-3">
      <div className={`w-10 h-10 ${color.bg} rounded-xl flex items-center justify-center`}>
        <Icon className={`w-5 h-5 ${color.text}`} />
      </div>
      {active && <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full ${color.bg} ${color.text}`}>Viewing</span>}
    </div>
    <p className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{count}</p>
    <p className="text-xs font-bold text-slate-600 mt-0.5">{label}</p>
    <p className={`text-sm font-black mt-1 ${color.text}`}>₹{amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
    <p className="text-[10px] text-slate-400 mt-1">{sub}</p>
  </button>
);

const AdminDeliveryPerformance = () => {
  const token = localStorage.getItem('token');
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeBucket, setActiveBucket] = useState(null);
  const [drillOrders, setDrillOrders] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setActiveBucket(null);
    setDrillOrders([]);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/delivery-performance`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { start_date: startDate, end_date: endDate }
      });
      setSummary(res.data);
    } catch {
      toast.error('Failed to load delivery performance');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, token]);

  useEffect(() => { fetchSummary(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCardClick = async (bucket) => {
    if (activeBucket === bucket) { setActiveBucket(null); setDrillOrders([]); return; }
    setActiveBucket(bucket);
    setDrillLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/delivery-performance/orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { start_date: startDate, end_date: endDate, bucket }
      });
      setDrillOrders(res.data.orders || []);
    } catch {
      toast.error('Failed to load orders');
    } finally {
      setDrillLoading(false);
    }
  };

  const bucketLabel = { on_time: 'On-Time Delivered', late: 'Late Delivered', unpaid: 'Delivered but Unpaid' };

  return (
    <div className="space-y-4 pb-24 md:pb-4" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-violet-700 rounded-[2rem] px-6 py-5 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
            <Gauge className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>Delivery Performance</h1>
            <p className="text-blue-100 text-sm">On-time vs late deliveries, and unpaid amounts, for a date range</p>
          </div>
        </div>
      </div>

      {/* Date range */}
      <div className="bg-white border border-slate-200 rounded-[2rem] px-5 py-4 shadow-sm flex items-center gap-3 flex-wrap">
        <div>
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Start Date</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
            className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:border-blue-500" />
        </div>
        <div>
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">End Date</label>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
            className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:border-blue-500" />
        </div>
        <button onClick={fetchSummary} disabled={loading}
          className="mt-4 flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Apply
        </button>
      </div>

      {loading && !summary ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : summary && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard label="On-Time Delivered" sub="delivered on or before due date"
              count={summary.on_time.count} amount={summary.on_time.amount}
              color={{ bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-400' }}
              icon={CheckCircle} active={activeBucket === 'on_time'} onClick={() => handleCardClick('on_time')} />
            <KpiCard label="Late Delivered" sub="delivered after due date"
              count={summary.late.count} amount={summary.late.amount}
              color={{ bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-400' }}
              icon={AlertTriangle} active={activeBucket === 'late'} onClick={() => handleCardClick('late')} />
            <KpiCard label="Delivered but Unpaid" sub="payment still pending"
              count={summary.unpaid.count} amount={summary.unpaid.amount}
              color={{ bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-400' }}
              icon={IndianRupee} active={activeBucket === 'unpaid'} onClick={() => handleCardClick('unpaid')} />
            <KpiCard label="Total Delivered" sub="all delivered orders in range"
              count={summary.total.count} amount={summary.total.amount}
              color={{ bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-400' }}
              icon={Package} active={false} onClick={() => {}} />
          </div>

          {/* Drill-down */}
          {activeBucket && (
            <div className="bg-white border border-slate-200 rounded-[2rem] overflow-hidden shadow-sm">
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                <p className="text-sm font-black text-slate-700">{bucketLabel[activeBucket]} — {drillOrders.length} order{drillOrders.length !== 1 ? 's' : ''}</p>
                {drillLoading && <Loader2 className="w-4 h-4 animate-spin text-blue-600" />}
              </div>
              {!drillLoading && drillOrders.length === 0 && (
                <p className="text-center text-sm text-slate-400 py-10">No orders in this bucket for the selected range</p>
              )}
              {!drillLoading && drillOrders.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] font-black text-slate-400 uppercase tracking-widest bg-slate-50">
                        <th className="px-4 py-2.5">Customer</th>
                        <th className="px-4 py-2.5">Invoice</th>
                        <th className="px-4 py-2.5">Due</th>
                        <th className="px-4 py-2.5">Delivered</th>
                        <th className="px-4 py-2.5">Delay</th>
                        <th className="px-4 py-2.5">Amount</th>
                        <th className="px-4 py-2.5">Payment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drillOrders.map((o, i) => (
                        <tr key={o.id} className={`border-t border-slate-100 ${i % 2 === 1 ? 'bg-slate-50/50' : ''}`}>
                          <td className="px-4 py-2.5">
                            <p className="font-semibold text-slate-800">{o.customer_name}</p>
                            <p className="text-[10px] text-slate-400 flex items-center gap-1"><Phone className="w-2.5 h-2.5" />{o.customer_phone}</p>
                          </td>
                          <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{o.invoice_number || '—'}</td>
                          <td className="px-4 py-2.5 text-xs text-slate-500">{safeFormat(o.expected_delivery_date, 'dd MMM, h:mm a')}</td>
                          <td className="px-4 py-2.5 text-xs text-slate-500">{safeFormat(o.delivery_date, 'dd MMM, h:mm a')}</td>
                          <td className="px-4 py-2.5">
                            {o.delay_hours > 0 ? (
                              <span className="flex items-center gap-1 text-xs font-bold text-red-600"><Clock className="w-3 h-3" />{o.delay_hours}h late</span>
                            ) : <span className="text-xs text-emerald-600 font-semibold">On time</span>}
                          </td>
                          <td className="px-4 py-2.5 font-bold text-slate-800">₹{o.total_amount?.toFixed(0)}</td>
                          <td className="px-4 py-2.5">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${o.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                              {o.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminDeliveryPerformance;
